<template>
    <div>
      <InventoryHeaderTab />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Create Stock Adjustment</h5>
          <p class="page-sub">Adjust stock levels across your inventory</p>
        </div>
        <button type="button" @click="$router.go(-1)" class="btn-outline">
          <font-awesome-icon icon="arrow-left" /> Back
        </button>
      </div>
  
      <!-- Validation Errors -->
      <div v-if="validationErrors?.length" class="error-banner">
        <font-awesome-icon icon="exclamation-circle" class="error-icon" />
        <ul class="mb-0 ps-1">
          <li v-for="(err, i) in validationErrors" :key="i">{{ err }}</li>
        </ul>
      </div>
  
      <form @submit.prevent="saveAdjustment">
  
        <!-- Header Info -->
        <div class="form-card mb-4">
          <div class="section-header">
            <div class="section-icon section-icon--blue">
              <font-awesome-icon icon="sliders-h" />
            </div>
            <div>
              <p class="section-title">Adjustment Details</p>
              <p class="section-sub">Select date and the store to adjust stock for</p>
            </div>
          </div>
  
          <div class="fields-grid fields-grid--2">
            <div class="field-group">
              <label class="field-label">Date & Time</label>
              <div class="datepicker-wrap">
                <font-awesome-icon icon="calendar" class="datepicker-icon" />
                <Datepicker v-model="date" :enableTimePicker="true" class="clean-datepicker" />
              </div>
            </div>
            <div class="field-group">
              <label class="field-label">Select Store <span class="req">*</span></label>
              <Multiselect
                v-model="store_id"
                label="name"
                :options="getStore"
                placeholder="Choose a store"
                :searchable="true"
                :create-option="false"
                :close-on-select="true"
                @select="getProductInStore(store_id)"
                class="custom-multiselect"
              />
            </div>
          </div>
        </div>
  
        <!-- Products Table -->
        <div class="form-card mb-4">
          <div class="section-header">
            <div class="section-icon section-icon--teal">
              <font-awesome-icon icon="boxes" />
            </div>
            <div>
              <p class="section-title">Products</p>
              <p class="section-sub">Add products and specify adjustment type and quantity</p>
            </div>
          </div>
  
          <div class="table-scroll-wrap">
            <table class="adj-table">
              <thead>
                <tr>
                  <th style="min-width:200px">Product</th>
                  <th style="min-width:90px">Avail. Qty</th>
                  <th style="min-width:150px">Variant / UOM</th>
                  <th style="min-width:120px">Cost Price</th>
                  <th style="min-width:90px">Qty</th>
                  <th style="min-width:160px">Reason</th>
                  <th style="min-width:140px">Type</th>
                  <th style="min-width:110px">Total</th>
                  <th style="min-width:44px"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(p, index) in product" :key="index">
  
                  <td>
                    <Multiselect
                      v-model="p.product_id"
                      label="name"
                      :options="getProducts"
                      :canClear="false"
                      :searchable="true"
                      :create-option="false"
                      :close-on-select="true"
                      @select="handleChange(p.product_id, index)"
                      placeholder="Select product"
                      class="custom-multiselect custom-multiselect--sm"
                    />
                  </td>
  
                  <td>
                    <input disabled type="text" class="tbl-input tbl-input--center" v-model="p.available_qty" />
                  </td>
  
                  <td>
                    <select v-if="p.variant.length > 0" class="tbl-input tbl-select" v-model="p.product_variant_id" @change="getVariantCost(p)">
                      <option disabled value="">Select variant</option>
                      <option v-for="variant in p.variant" :key="variant.id" :value="variant.id">{{ variant.variant_name }}</option>
                    </select>
                    <input v-else type="text" class="tbl-input" :value="p.unit_of_measurement" disabled />
                  </td>
  
                  <td>
                    <input type="number" class="tbl-input" v-model="p.cost_price" @keyup="calculateAmount(p)" />
                  </td>
  
                  <td>
                    <input type="text" min="0" class="tbl-input tbl-input--center" v-model="p.quantity"
                      @keyup="calculateAmount(p)" onkeypress="return /^[0-9]+$/i.test(event.key)" />
                  </td>
  
                  <td>
                    <input type="text" class="tbl-input" v-model="p.reason" placeholder="e.g. Damaged" />
                  </td>
  
                  <td>
                    <select class="tbl-input tbl-select" v-model="p.adjustment_type" @change="adjustmentType(p)">
                      <option disabled value="">Type</option>
                      <option value="increment">
                        ▲ Increment
                      </option>
                      <option value="decrement">
                        ▼ Decrement
                      </option>
                    </select>
                  </td>
  
                  <td>
                    <input disabled type="text" class="tbl-input tbl-input--total"
                      :class="parseFloat(p.total_price) < 0 ? 'tbl-input--neg' : parseFloat(p.total_price) > 0 ? 'tbl-input--pos' : ''"
                      v-model="p.total_price" />
                  </td>
  
                  <td>
                    <button class="btn-remove-row" @click.prevent="removeProduct(index)" type="button" title="Remove row">
                      <font-awesome-icon icon="times" />
                    </button>
                  </td>
  
                </tr>
              </tbody>
            </table>
          </div>
  
          <!-- Add Row + Grand Total -->
          <div class="table-footer-actions">
            <button class="btn-add-row" type="button" @click="addProduct">
              <font-awesome-icon icon="plus" class="me-2" /> Add Product
            </button>
  
            <div class="grand-total">
              <span class="grand-total-label">Grand Total</span>
              <span class="grand-total-value" :class="parseFloat(grandTotal()) < 0 ? 'total-neg' : 'total-pos'">
                {{ $filters.formatMoney(grandTotal(), currentUserCurrency?.symbol) }}
              </span>
            </div>
          </div>
        </div>
  
        <!-- Footer -->
        <div class="form-footer">
          <button type="button" @click="$router.go(-1)" class="btn-outline">Cancel</button>
          <button type="submit" class="btn-primary-action" :disabled="crudLoading">
            <span v-if="crudLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
            <font-awesome-icon v-else icon="check" class="me-2" />
            {{ crudLoading ? 'Saving...' : 'Save Adjustment' }}
          </button>
        </div>
  
      </form>
    </div>
  </template>
  
  <script>
  import InventoryHeaderTab from "./component/inventoryheadertab.vue";
  import { ref } from "@vue/reactivity";
  import { onMounted } from "@vue/runtime-core";
  import axios from "axios";
  import { useToast } from "vue-toastification";
  import { useRouter } from "vue-router";
  import { useReusable } from "../../../lib/reusable";
  import Multiselect from '@vueform/multiselect';
  import { reusableMixin } from '../../../lib/reusableMixin';
  
  const EMPTY_PRODUCT = () => ({
    product_id: '', available_qty: 0, cost_price: '', new_cost_price: '',
    selling_price: '', variant: [], product_variant_id: '',
    quantity: '', reason: '', adjustment_type: '', total_price: (0).toFixed(2),
  });
  
  export default {
    components: { InventoryHeaderTab, Multiselect },
    mixins: [reusableMixin],
  
    setup() {
      const crudLoading      = ref(false);
      const toast            = useToast();
      const router           = useRouter();
      const validationErrors = ref([]);
      const getVariants      = ref('');
      const date             = ref(new Date());
      const store_id         = ref(null);
      const getProducts      = ref([]);
      const product          = ref(Array.from({ length: 5 }, EMPTY_PRODUCT));
  
      const { removeProduct, validateInput, getStore, allStore } = useReusable(product);
  
      const addProduct = () => product.value.push({ ...EMPTY_PRODUCT(), quantity: 1 });
  
      const clearFields = (p) => Object.assign(p, EMPTY_PRODUCT());
  
      const getProductInStore = async (storeId) => {
        const res      = await axios.get(`/admin/store/product-in-store/${storeId}`);
        const products = res?.data?.data?.data;
        getProducts.value = products.map(p => ({
          value: p.id,
          name: p.bar_code ? `${p.product_name} (${p.bar_code})` : p.product_name,
        }));
        product.value.forEach(p => clearFields(p));
      };
  
      const adjustmentType = (p) => {
        p.total_price = p.adjustment_type === 'decrement'
          ? parseFloat(-Math.abs(p.total_price)).toFixed(2)
          : parseFloat(Math.abs(p.total_price)).toFixed(2);
        return p.total_price;
      };
  
      const saveAdjustment = async () => {
        crudLoading.value = true;
        const valid_products = product.value.filter(p => p.product_id !== '');
        axios.post('/inventory/store-adjustment', {
          store_id: store_id.value,
          time: date.value.toTimeString().slice(0, 8),
          date: date.value.toISOString().split('T')[0],
          adjustment_item: valid_products,
        }).then(res => {
          crudLoading.value = false; router.push('/all-stock-adjustment'); toast.success(res.data.message);
        }).catch(error => {
          crudLoading.value = false;
          if (error.response.status == 422) { toast.warning('There are errors in your input'); validationErrors.value = Array.isArray(error.response.data.message) ? error.response.data.message : (error.response.data.message ? [String(error.response.data.message)] : []); }
          if (error.response.status == 403) toast.error(error.response.data.message);
          if (error.response.status == 400) { validationErrors.value = Array.isArray(error.response.data.message) ? error.response.data.message : (error.response.data.message ? [String(error.response.data.message)] : []); toast.error('Bad request, please check your input'); }
          if (error.response.status == 500) toast.error('An error occurred, please try again');
        });
      };
  
      const handleChange = async (id, index) => {
        const res            = await axios.get(`/inventory/product/${id}?store_id=${store_id.value}`);
        const single_product = res?.data?.data;
        const occurrences    = product.value.filter(v => v.product_id == single_product.id);
  
        if (occurrences.length > 1) {
          Object.assign(product.value[index], EMPTY_PRODUCT());
          toast.warning('Product already selected'); return;
        }
  
        if (product.value.filter(v => v.product_id === '').length === 0) addProduct();
  
        product.value[index].cost_price          = parseFloat(single_product.cost_price).toFixed(2);
        product.value[index].new_cost_price      = single_product.cost_price;
        product.value[index].selling_price       = single_product.selling_price;
        product.value[index].available_qty       = single_product.store_qty;
        product.value[index].total_price         = parseFloat(single_product.cost_price).toFixed(2);
        product.value[index].variant             = single_product.variants;
        product.value[index].unit_of_measurement = single_product.unit_of_measurement;
        product.value[index].product_variant_id  = '';
        product.value[index].quantity            = 1;
      };
  
      const getVariantCost = (p) => {
        const variant     = p.variant.find(({ id }) => id == p.product_variant_id);
        const qty         = p.quantity === '' ? 0 : p.quantity;
        p.cost_price      = (p.new_cost_price * parseFloat(variant.variant_quantity)).toFixed(2);
        p.total_price     = (p.cost_price * parseFloat(qty)).toFixed(2);
        adjustmentType(p);
      };
  
      const calculateAmount = (p) => {
        p.total_price = (parseFloat(p.cost_price) * parseFloat(p.quantity)).toFixed(2);
        if (isNaN(p.total_price)) p.total_price = 0;
        else adjustmentType(p);
      };
  
      const grandTotal = () => product.value.reduce((a, b) => (parseFloat(a) + parseFloat(b.total_price)).toFixed(2), 0);
  
      onMounted(() => { allStore(); });
  
      return {
        grandTotal, crudLoading, getStore, getProducts, getVariants, date, product,
        addProduct, removeProduct, saveAdjustment, handleChange, validationErrors,
        store_id, getVariantCost, getProductInStore, validateInput,
        adjustmentType, calculateAmount,
      };
    },
  };
  </script>
  
  <style src="@vueform/multiselect/themes/default.css"></style>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
  .page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub    { font-size:13px; color:#7a90b0; margin:0; }
  
  /* ── Error Banner ── */
  .error-banner { display:flex; align-items:flex-start; gap:10px; background:#fff5f5; border:1px solid #fac5c5; border-radius:10px; padding:12px 16px; margin-bottom:20px; color:#e03535; font-size:13px; }
  .error-icon   { font-size:16px; margin-top:2px; flex-shrink:0; }
  
  /* ── Form Card ── */
  .form-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; }
  
  /* ── Section Header ── */
  .section-header { display:flex; align-items:center; gap:14px; margin-bottom:18px; }
  .section-icon { width:42px; height:42px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:17px; flex-shrink:0; }
  .section-icon--blue { background:rgba(0,74,173,.1); color:#004AAD; }
  .section-icon--teal { background:rgba(13,148,136,.1); color:#0d9488; }
  .section-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .section-sub   { font-size:12px; color:#7a90b0; margin:0; }
  
  /* ── Fields ── */
  .fields-grid    { display:grid; gap:16px; }
  .fields-grid--2 { grid-template-columns:repeat(2,1fr); }
  .field-group    { display:flex; flex-direction:column; gap:6px; }
  .field-label    { font-size:13px; font-weight:600; color:#1a2b45; }
  .req            { color:#e03535; }
  
  /* ── Datepicker ── */
  .datepicker-wrap { position:relative; }
  .datepicker-icon { position:absolute; left:11px; top:50%; transform:translateY(-50%); color:#7a90b0; font-size:13px; z-index:2; pointer-events:none; }
  :deep(.clean-datepicker) { border:1px solid #dde5f0 !important; border-radius:8px !important; background:#fff !important; padding-left:32px !important; }
  :deep(.clean-datepicker input) { font-size:13.5px; color:#1a2b45; }
  :deep(.clean-datepicker.is-active) { border-color:#004AAD !important; box-shadow:0 0 0 3px rgba(0,74,173,.08) !important; }
  
  /* ── Multiselect ── */
  :deep(.custom-multiselect) {
    --ms-border-color:#dde5f0; --ms-border-radius:8px;
    --ms-font-size:13.5px; --ms-tag-bg:#edf4ff; --ms-tag-color:#004AAD;
    --ms-ring-color:rgba(0,74,173,.08);
  }
  :deep(.custom-multiselect.is-active) { --ms-border-color:#004AAD; }
  :deep(.custom-multiselect--sm) { --ms-py:6px; --ms-font-size:13px; }
  
  /* ── Products Table ── */
  .table-scroll-wrap { overflow-x:auto; margin:0 -4px; }
  
  .adj-table { width:100%; border-collapse:collapse; }
  .adj-table thead tr { background:#f8fafc; }
  .adj-table thead th { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:10px 8px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .adj-table tbody tr { border-bottom:1px solid #f0f4f8; }
  .adj-table tbody tr:last-child { border-bottom:none; }
  .adj-table tbody tr:hover { background:#fafbfc; }
  .adj-table tbody td { padding:8px; vertical-align:middle; }
  
  /* Table inputs */
  .tbl-input {
    width:100%; padding:7px 9px; border:1px solid #dde5f0; border-radius:7px;
    font-size:13px; color:#1a2b45; background:#fff; outline:none;
    transition:border-color .15s;
  }
  .tbl-input:focus  { border-color:#004AAD; box-shadow:0 0 0 2px rgba(0,74,173,.08); }
  .tbl-input:disabled { background:#f8fafc; color:#aab8cc; }
  .tbl-input::placeholder { color:#aab8cc; }
  .tbl-select { appearance:auto; cursor:pointer; }
  .tbl-input--center { text-align:center; }
  .tbl-input--total { font-weight:700; }
  .tbl-input--pos   { color:#16a34a; border-color:#bbf7d0; background:#f0fdf4; }
  .tbl-input--neg   { color:#e03535; border-color:#fac5c5; background:#fff5f5; }
  
  /* Remove row button */
  .btn-remove-row {
    width:30px; height:30px; border-radius:7px;
    background:#fff0f0; border:1px solid #fac5c5;
    color:#e03535; display:flex; align-items:center; justify-content:center;
    font-size:12px; cursor:pointer; transition:background .15s;
  }
  .btn-remove-row:hover { background:#ffe0e0; }
  
  /* Table footer */
  .table-footer-actions {
    display:flex; align-items:center; justify-content:space-between;
    margin-top:16px; flex-wrap:wrap; gap:12px;
  }
  
  .btn-add-row {
    display:inline-flex; align-items:center;
    background:#edf4ff; color:#004AAD;
    border:1px solid #c5d6f5; font-size:13px; font-weight:600;
    padding:8px 18px; border-radius:8px; cursor:pointer; transition:background .15s;
  }
  .btn-add-row:hover { background:#d6e4fa; }
  
  /* Grand Total */
  .grand-total { display:flex; align-items:center; gap:12px; background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:12px 20px; }
  .grand-total-label { font-size:13px; font-weight:600; color:#7a90b0; }
  .grand-total-value { font-size:20px; font-weight:800; color:#1a2b45; }
  .total-pos { color:#16a34a; }
  .total-neg { color:#e03535; }
  
  /* ── Footer ── */
  .form-footer { display:flex; align-items:center; justify-content:flex-end; gap:10px; margin-top:4px; padding-top:4px; }
  .btn-primary-action { display:inline-flex; align-items:center; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 24px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
  .btn-primary-action:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 20px; border-radius:8px; cursor:pointer; transition:background .15s; }
  .btn-outline:hover { background:#f4f7fb; }
  
  /* ── Responsive ── */
  @media(max-width:768px) {
    .fields-grid--2 { grid-template-columns:1fr; }
    .form-card { padding:16px; }
    .form-footer { flex-direction:column; }
    .btn-primary-action,.btn-outline { width:100%; justify-content:center; }
    .table-footer-actions { flex-direction:column; align-items:stretch; }
    .grand-total { justify-content:center; }
  }
  </style>